import React, { useState, useEffect } from 'react';
import { moodAPI } from '../services/api';
import { Brain, Plus, CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Simple Calendar Component for selecting past dates
const MoodCalendar = ({ selectedDate, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today for comparison

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (next <= today) {
      setCurrentMonth(next);
    }
  };

  const isDateDisabled = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date > today;
  };

  const isDateSelected = (day) => {
    if (!selectedDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const todayDate = new Date();
    return date.toDateString() === todayDate.toDateString();
  };

  const handleDateClick = (day) => {
    if (isDateDisabled(day)) return;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day, 12, 0, 0);
    onDateSelect(date);
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const canGoNext = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) <= today;

  return (
    <div className="rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid rgba(255, 165, 0, 0.2)' }} data-testid="mood-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="p-2 rounded-lg transition-colors"
          style={{ background: 'rgba(255, 165, 0, 0.1)' }}
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: '#777777' }} />
        </button>
        <span className="font-semibold" style={{ color: '#0A0A0A' }}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          disabled={!canGoNext}
          className={`p-2 rounded-lg transition-colors ${!canGoNext ? 'opacity-30 cursor-not-allowed' : ''}`}
          style={{ background: canGoNext ? 'rgba(255, 165, 0, 0.1)' : 'transparent' }}
          data-testid="calendar-next-month"
        >
          <ChevronRight className="w-5 h-5" style={{ color: '#777777' }} />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-xs font-medium py-1" style={{ color: '#777777' }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the 1st */}
        {Array.from({ length: startingDay }).map((_, idx) => (
          <div key={`empty-${idx}`} className="h-9" />
        ))}
        
        {/* Days of the month */}
        {Array.from({ length: daysInMonth }).map((_, idx) => {
          const day = idx + 1;
          const disabled = isDateDisabled(day);
          const selected = isDateSelected(day);
          const todayHighlight = isToday(day);

          return (
            <button
              type="button"
              key={day}
              onClick={() => handleDateClick(day)}
              disabled={disabled}
              className="h-9 w-9 rounded-full text-sm font-medium transition-all"
              style={{
                color: disabled ? '#D1CDC5' : selected ? '#FFFFFF' : '#0A0A0A',
                background: selected ? '#FF8C00' : todayHighlight ? 'rgba(255, 165, 0, 0.15)' : 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
              data-testid={`calendar-day-${day}`}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Selected Date Display */}
      {selectedDate && (
        <div className="mt-4 pt-3 text-center" style={{ borderTop: '1px solid rgba(255, 165, 0, 0.2)' }}>
          <p className="text-sm" style={{ color: '#777777' }}>
            Logging mood for: <span className="font-semibold" style={{ color: '#FF8C00' }}>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export const MoodJournal = () => {
  const [moods, setMoods] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    mood_scale: 3,
    note: '',
    tags: []
  });

  useEffect(() => {
    fetchMoods();
  }, []);

  const fetchMoods = async () => {
    try {
      const response = await moodAPI.getAll(30);
      setMoods(response.data);
    } catch (error) {
      toast.error('Failed to load mood entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Include the selected date in the API call
      await moodAPI.create({
        ...formData,
        entry_date: selectedDate.toISOString()
      });
      toast.success('Mood logged successfully!');
      setShowForm(false);
      setFormData({ mood_scale: 3, note: '', tags: [] });
      setSelectedDate(new Date()); // Reset to today
      fetchMoods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log mood');
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const chartData = moods.slice(0, 7).reverse().map(mood => ({
    date: new Date(mood.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    mood: mood.mood_scale
  }));

  return (
    <div className="min-h-screen py-8" data-testid="mood-journal-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#0A0A0A' }}>
              Mood Journal
            </h1>
            <p style={{ color: '#555555' }}>Track your emotional wellness over time</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center space-x-2 px-6 py-3 rounded-full btn-primary-cta hover:scale-105"
            data-testid="add-mood-button"
          >
            <Plus className="w-5 h-5" />
            <span>Log Mood</span>
          </button>
        </div>

        {showForm && (
          <div className="feature-card mb-8" data-testid="mood-form">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#0A0A0A' }}>
              How are you feeling?
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Calendar Section */}
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2" style={{ color: '#0A0A0A' }}>
                  <CalendarIcon className="w-4 h-4" />
                  Select Date (You can log for past dates)
                </label>
                <MoodCalendar 
                  selectedDate={selectedDate} 
                  onDateSelect={handleDateSelect} 
                />
              </div>

              {/* Mood Scale */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A0A' }}>
                  Mood Scale (1-5)
                </label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, mood_scale: value })}
                      className="w-16 h-16 rounded-full flex items-center justify-center text-2xl transition-transform"
                      style={{
                        background: formData.mood_scale === value ? '#FF8C00' : 'rgba(255, 165, 0, 0.15)',
                        color: formData.mood_scale === value ? '#FFFFFF' : '#555555',
                        transform: formData.mood_scale === value ? 'scale(1.1)' : 'scale(1)'
                      }}
                      data-testid={`mood-scale-${value}`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: '#777777' }}>1 = Very Low, 5 = Excellent</p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A0A' }}>
                  Do you want to share? (Optional)
                </label>
                <textarea
                  className="w-full px-4 py-3 rounded-xl resize-none input-sunrise"
                  rows="3"
                  placeholder="What's on your mind?"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  data-testid="mood-note-input"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full font-semibold btn-primary-cta"
                  data-testid="mood-submit-button"
                >
                  Save Entry
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedDate(new Date());
                  }}
                  className="px-6 py-3 rounded-full font-semibold btn-secondary"
                  data-testid="mood-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {chartData.length > 0 && (
          <div className="feature-card mb-8" data-testid="mood-chart">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#0A0A0A' }}>
              Last 7 Days Trend
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 165, 0, 0.2)" />
                <XAxis dataKey="date" stroke="#777777" />
                <YAxis domain={[0, 5]} stroke="#777777" />
                <Tooltip />
                <Line type="monotone" dataKey="mood" stroke="#FF8C00" strokeWidth={3} dot={{ fill: '#FF8C00', r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="space-y-4">
          {moods.map((mood) => (
            <div key={mood.id} className="feature-card" data-testid="mood-entry">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold"
                    style={{
                      background: mood.mood_scale >= 4 ? 'rgba(34, 197, 94, 0.15)' :
                        mood.mood_scale === 3 ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      color: mood.mood_scale >= 4 ? '#22C55E' :
                        mood.mood_scale === 3 ? '#EAB308' : '#EF4444'
                    }}
                  >
                    {mood.mood_scale}
                  </div>
                  <div>
                    <div className="font-semibold" style={{ color: '#0A0A0A' }}>
                      {mood.mood_scale >= 4 ? 'Great Day!' : mood.mood_scale === 3 ? 'Okay Day' : 'Tough Day'}
                    </div>
                    <div className="text-sm" style={{ color: '#777777' }}>
                      {new Date(mood.created_at).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>
                </div>
              </div>
              {mood.note && (
                <p className="mt-3 pl-15" style={{ color: '#333333' }}>{mood.note}</p>
              )}
              {mood.tags && mood.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 pl-15">
                  {mood.tags.map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 rounded-full text-sm" style={{ background: 'rgba(255, 140, 0, 0.15)', color: '#FF8C00' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {!loading && moods.length === 0 && (
          <div className="text-center py-12 feature-card">
            <Brain className="w-16 h-16 mx-auto mb-4" style={{ color: '#D1CDC5' }} />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#0A0A0A' }}>
              No mood entries yet
            </h3>
            <p className="mb-4" style={{ color: '#777777' }}>Start tracking your emotional wellness today</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 rounded-full font-semibold btn-primary-cta hover:scale-105"
            >
              Log Your First Mood
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
